export * from './aiModels.js';
export * from './aiProfile.js';
export * from './aiCapabilityClass.js';
export * from './aiRequestModel.js';
export * from './consoleSql.js';
export * from './activeSprintStatus.js';
export const USER_ROLES = ['admin', 'manager', 'designer', 'user'];
export const GROUP_ROLES = ['owner', 'admin', 'member'];
export const MEMORY_CATEGORIES = ['startup', 'general'];
export const DEFAULT_PAGINATION = {
    page: 1,
    perPage: 20,
    sortOrder: 'desc',
};
export const API_PREFIX = '/api';
export const PERMISSION_MODULES = [
    'users',
    'groups',
    'notes',
    'files',
    'categories',
];
/**
 * Default permission sets per role. Roadmap #98 added the `module.{x}.access`
 * axis: each atlas module node carries `requiredPermissions: ["module.X.access"]`,
 * and `useModules` filters the sidebar/dashboard against the user's effective
 * permission set. Admin keeps the wildcard; other roles get a minimal
 * starting kit and can be expanded per-user via the Permissions tab.
 *
 * Plugin permission'ları artık manifest permissionDefinitions tarafından yönetilir;
 * bu map core/sistem permission'larını tutar (gh#723). An extension's keys are its own
 * K4 `roleDefaults` (W4-K5a); the api reads core ∪ extensions via getRoleDefaultPermissions().
 */
export const ROLE_DEFAULT_PERMISSIONS = {
    admin: ['*'],
    manager: [
        'users.view',
        'users.update',
        'groups.view',
        'groups.create',
        'groups.update',
        'groups.manage_members',
        'notes.view',
        'notes.create',
        'notes.update',
        'notes.delete',
        'notes.share',
        'notes.manage',
        'files.view',
        'files.create',
        'files.update',
        'files.delete',
        'files.share',
        'categories.view',
        'categories.create',
        'categories.update',
        // Entity OS — project entity CRUD (umbrella #421, roadmap #429). Manager gets
        // the full CRUD set. Keys mirror entity.project.tools.ts `requiredPermission`
        // exactly; a drift here means the dispatch PEP grant never resolves.
        'entity.project.create',
        'entity.project.update',
        'entity.project.get',
        'entity.project.list',
        // Entity OS — organization entity (umbrella #421, PR-3a). Manager gets the
        // FULL CRUD set. Keys mirror organization.tools.ts `requiredPermission`.
        'entity.organization.create',
        'entity.organization.update',
        'entity.organization.get',
        'entity.organization.list',
        // Entity OS — brand entity (umbrella #421, PR-3b). Manager gets the FULL CRUD
        // set. Keys mirror brand.tools.ts `requiredPermission`.
        'entity.brand.create',
        'entity.brand.update',
        'entity.brand.get',
        'entity.brand.list',
        // Entity OS — product (SKU) entity (umbrella #421, PR-4). Manager gets the
        // FULL CRUD set. Keys mirror product.tools.ts `requiredPermission`.
        'entity.product.create',
        'entity.product.update',
        'entity.product.get',
        'entity.product.list',
        // Entity OS — design system entity (umbrella #421, PR-4 Faz-5). Manager gets the
        // FULL CRUD set. Keys mirror designSystem.tools.ts `requiredPermission`.
        'entity.designSystem.create',
        'entity.designSystem.update',
        'entity.designSystem.get',
        'entity.designSystem.list',
        // Entity OS — task entity (umbrella #421, Faz-6 PR-2). Manager gets the FULL CRUD
        // set. Keys mirror task.tools.ts `requiredPermission`.
        'entity.task.create',
        'entity.task.update',
        'entity.task.get',
        'entity.task.list',
        // E3 CRM party model (roadmap #457, PR-2/6 + delete tools gh#1420). Manager gets the FULL
        // CRUD+delete set for both the person (human party) and the party role (customer/supplier
        // "cari" record). Keys mirror person.tools.ts / partyRole.tools.ts `requiredPermission`
        // (partyRole is camelCase, matching the tool name — see the parity test). `.delete` is a
        // soft-delete (person delete is blocked while live party roles reference it).
        'entity.person.create',
        'entity.person.update',
        'entity.person.get',
        'entity.person.list',
        'entity.person.delete',
        'entity.partyRole.create',
        'entity.partyRole.update',
        'entity.partyRole.get',
        'entity.partyRole.list',
        'entity.partyRole.delete',
        // E3 CRM party model (roadmap #457, PR-5/6 — service slice). Manager gets the FULL CRUD
        // set for the activity (customer-interaction / service record) entity. Keys mirror
        // activity.tools.ts `requiredPermission`.
        'entity.activity.create',
        'entity.activity.update',
        'entity.activity.get',
        'entity.activity.list',
        // Entity OS — grouped aggregate read (ERP E1 PR-4, roadmap #455). A DEDICATED
        // capability (NOT gated by `.list`): a manager who can list an entity can also
        // run grouped COUNT/SUM/AVG/MIN/MAX over the SAME tenant-scoped rows. One key per
        // entity, mirroring aggregate.tools.ts `requiredPermission` (drift → grant never
        // resolves; the parity test guards it).
        'entity.project.aggregate',
        'entity.organization.aggregate',
        'entity.brand.aggregate',
        'entity.product.aggregate',
        'entity.designSystem.aggregate',
        'entity.task.aggregate',
        // E3 CRM party model aggregate parity (gh#1404). Manager holds .list on both person
        // and party role above, so it also gets the DEDICATED .aggregate read over the SAME
        // tenant-scoped rows. `partyRole` is camelCase (mirrors the tool name).
        'entity.person.aggregate',
        'entity.partyRole.aggregate',
        // Entity OS — generic relation primitive (Phase-4, PR-2/5). Manager gets the
        // full link/unlink/read set. `entity.relation.list` maps to the read key
        // entity.relation.get (no separate .list key). Keys mirror relation.tools.ts.
        'entity.relation.link',
        'entity.relation.unlink',
        'entity.relation.get',
        // Saved queries (ERP E1 PR-5, roadmap #455). Every role gets the full CRUD+run
        // set (a saved query is a personal artefact, owner-scoped). Keys mirror
        // savedQuery.tools.ts `requiredPermission` (drift → grant never resolves; the
        // saved-query-role-defaults parity test guards it). `query.saved.share_tenant` is
        // the EXTRA capability to publish a query at visibility='tenant' — manager + admin
        // ONLY (designer / user may create PRIVATE queries but not share to the tenant).
        'query.saved.create',
        'query.saved.get',
        'query.saved.list',
        'query.saved.update',
        'query.saved.delete',
        'query.saved.run',
        // CSV/XLSX export (ERP E1 PR-6/7) — every role that can `run` a saved query can also
        // export its result set (a bulk read of the SAME tenant-scoped rows).
        'query.saved.export',
        'query.saved.share_tenant',
        // Compositor × Entity OS — saved compositions (G2, roadmap #485/#487). A saved
        // composition is a NAMED, launchable Gen-UI WindowSpec (owner-scoped, two-tier
        // visibility). Every non-admin role gets the base list/get/save/resolve set (a
        // personal artefact); `composer.compositions.share_tenant` is the EXTRA capability to
        // publish at visibility='tenant' — manager (+ admin via '*') ONLY (designer / user may
        // save PRIVATE compositions but not share to the whole tenant). The four base keys
        // mirror savedComposition.tools.ts `requiredPermission`; the share key mirrors the
        // compositions route + save-tool share gate (drift → grant never resolves; the
        // composer-saved-role-defaults parity test guards it).
        'composer.saved.list',
        'composer.saved.get',
        'composer.saved.save',
        'composer.saved.resolve',
        'composer.compositions.share_tenant',
        // Compositor × Entity OS — hub → compositor-agent hand-off (G3, roadmap #488). The
        // `composer.delegate` tool resolves-or-composes a UI intent through the narrow compositor
        // whitelist; every non-admin role gets it (admin via '*'). Key mirrors the composer.delegate
        // tool `requiredPermission` + the seed-compositor-agent.ts permission row (drift → grant
        // never resolves; the compositorAgent.parity test guards it).
        'composer.delegate',
        // Staged CSV import (ERP E1 PR-7/7, roadmap #455). Manager (+ admin) ONLY — bulk
        // entity ingestion is a privileged capability (designer / user do NOT get it by
        // default). `entity.import.run` = upload+validate+preview; `entity.import.commit` =
        // replay rows through the entity gateway. Keys mirror seed-import-permissions.ts +
        // the route checkPermission (drift → grant never resolves; parity test guards it).
        // NOTE: committing also needs the per-entity `entity.<slug>.create|update` grant
        // (the per-row dispatch PEP), which manager already holds above.
        'entity.import.run',
        'entity.import.commit',
        // module-access axis (#98)
        'module.users.access',
        'module.groups.access',
        'module.notes.access',
        'module.files.access',
        'module.memory.access',
        'module.canvas.access',
        'module.ai.access',
        'module.atlas.access',
        'module.roadmap.access',
    ],
    designer: [
        'notes.view',
        'notes.create',
        'notes.update',
        'notes.share',
        'files.view',
        'files.create',
        'files.update',
        'files.share',
        'categories.view',
        // Entity OS — project entity (umbrella #421, roadmap #429). Designer is
        // read + write but NOT create (deliberate scope: designers refine existing
        // projects, they do not spin up new ones). Keys mirror entity.project.tools.ts.
        'entity.project.get',
        'entity.project.list',
        'entity.project.update',
        // Entity OS — organization entity (umbrella #421, PR-3a). Designer is
        // READ-ONLY: {get, list} but NOT update/create. An organization is a legal
        // entity; designers have no write business here (least-privilege). Designer
        // write capability arrives on Brand in a later PR — a deliberate divergence
        // from the Project mapping (where designer also has update).
        'entity.organization.get',
        'entity.organization.list',
        // Entity OS — brand entity (umbrella #421, PR-3b). Designer is read + write
        // but NOT create: {get, list, update}. Brand is the DESIGN domain — designers
        // refine brand foundation / voice / design tokens, so they get update here (a
        // DELIBERATE divergence from the Organization mapping, which is read-only
        // because an org is a legal entity). They still do not spin up new brands.
        'entity.brand.get',
        'entity.brand.list',
        'entity.brand.update',
        // Entity OS — product (SKU) entity (umbrella #421, PR-4). Designer is read +
        // write but NOT create: {get, list, update} — parity with Brand. Designers
        // refine SKU pricing / media / attributes, so they get update; they still do
        // not spin up new products.
        'entity.product.get',
        'entity.product.list',
        'entity.product.update',
        // Entity OS — design system entity (umbrella #421, PR-4 Faz-5). Designer gets
        // the FULL set INCLUDING create: {get, list, update, create}. A design system IS
        // the designer's primary artefact (a reusable theme / DTCG token bag), so unlike
        // Brand / Product / Unit (where designer has no create) designers may author
        // brand-new design systems — a deliberate, broader scope for the design domain's
        // own entity.
        'entity.designSystem.get',
        'entity.designSystem.list',
        'entity.designSystem.update',
        'entity.designSystem.create',
        // Entity OS — task entity (umbrella #421, Faz-6 PR-2). Designer is read + write
        // but NOT create: {get, list, update} — parity with Brand / Product / Unit.
        // Designers refine existing tasks (status / assignee / priority) but do not spin
        // up brand-new tasks (create is manager-only).
        'entity.task.get',
        'entity.task.list',
        'entity.task.update',
        // E3 CRM party model (roadmap #457, PR-2/6). Designer is READ-ONLY on both person and
        // party role: {get, list} but NOT update/create — and deliberately NOT delete (gh#1420).
        // Person is PII / business-contact data and party role is commercial "cari" data — a
        // designer has no write/delete business here (least-privilege), mirroring the read-only
        // Organization mapping (and diverging from Brand / Product / Unit where designer DOES
        // write). `entity.*.delete` is therefore MANAGER-only (manager section above).
        'entity.person.get',
        'entity.person.list',
        'entity.partyRole.get',
        'entity.partyRole.list',
        // E3 CRM party model (roadmap #457, PR-5/6 — service slice). Designer is READ-ONLY on the
        // activity entity: {get, list} but NOT update/create. An activity is customer-interaction /
        // service data, not a design artefact — least-privilege, mirroring the read-only Lead /
        // Person / Organization mapping.
        'entity.activity.get',
        'entity.activity.list',
        // Entity OS — grouped aggregate read (ERP E1 PR-4, roadmap #455). Designer has
        // `.list` on every entity above, so it also gets the DEDICATED `.aggregate` read
        // over the SAME tenant-scoped rows (aggregate is a read; no write implication).
        // One key per entity, mirroring aggregate.tools.ts `requiredPermission`.
        'entity.project.aggregate',
        'entity.organization.aggregate',
        'entity.brand.aggregate',
        'entity.product.aggregate',
        'entity.designSystem.aggregate',
        'entity.task.aggregate',
        // E3 CRM party model aggregate parity (gh#1404). Designer holds .list on person +
        // party role (read-only), so it also gets the DEDICATED .aggregate read (a read; no
        // write implication). `partyRole` is camelCase (mirrors the tool name).
        'entity.person.aggregate',
        'entity.partyRole.aggregate',
        // Entity OS — generic relation primitive (Phase-4, PR-2/5). Designer is
        // read-only on relations (entity.relation.get gates list too); designers
        // refine/inspect composition, they do not mutate the relation graph.
        'entity.relation.get',
        // Saved queries (ERP E1 PR-5, roadmap #455). Designer gets the full CRUD+run set
        // but NOT `query.saved.share_tenant` — a designer may save PRIVATE queries yet not
        // publish them to the whole tenant (share is manager/admin only).
        'query.saved.create',
        'query.saved.get',
        'query.saved.list',
        'query.saved.update',
        'query.saved.delete',
        'query.saved.run',
        // CSV/XLSX export (ERP E1 PR-6/7) — parity with `run` (bulk read of the same rows).
        'query.saved.export',
        // Compositor × Entity OS — saved compositions (G2, roadmap #485/#487). Designer gets
        // the base list/get/save/resolve set but NOT `composer.compositions.share_tenant` (may
        // save PRIVATE compositions, not publish to the whole tenant — share is manager/admin only).
        'composer.saved.list',
        'composer.saved.get',
        'composer.saved.save',
        'composer.saved.resolve',
        // Compositor × Entity OS — hub → compositor-agent hand-off (G3, roadmap #488).
        'composer.delegate',
        // module-access axis (#98) — designer is creative-tool focused
        'module.notes.access',
        'module.files.access',
        'module.canvas.access',
        'module.ui.system.access',
        'module.memory.access',
    ],
    user: [
        'users.view',
        'groups.view',
        'notes.view',
        'notes.create',
        'notes.update',
        'notes.delete',
        'notes.share',
        'files.view',
        'files.create',
        'files.update',
        'files.delete',
        'files.share',
        'categories.view',
        // Saved queries (ERP E1 PR-5, roadmap #455). Base user gets the full CRUD+run set
        // over its OWN saved queries but NOT `query.saved.share_tenant` (private only).
        'query.saved.create',
        'query.saved.get',
        'query.saved.list',
        'query.saved.update',
        'query.saved.delete',
        'query.saved.run',
        // CSV/XLSX export (ERP E1 PR-6/7) — parity with `run` (bulk read of the same rows).
        'query.saved.export',
        // Compositor × Entity OS — saved compositions (G2, roadmap #485/#487). Base user gets
        // the list/get/save/resolve set over its OWN compositions but NOT
        // `composer.compositions.share_tenant` (private only).
        'composer.saved.list',
        'composer.saved.get',
        'composer.saved.save',
        'composer.saved.resolve',
        // Compositor × Entity OS — hub → compositor-agent hand-off (G3, roadmap #488).
        'composer.delegate',
        // module-access axis (#98)
        'module.notes.access',
        'module.files.access',
        'module.memory.access',
    ],
};
export const NOTE_SHARE_ROLES = ['viewer', 'editor'];
export const NOTE_COLORS = [
    '#EF4444', '#F97316', '#F59E0B', '#22C55E',
    '#3B82F6', '#6366F1', '#A855F7', '#EC4899',
    '#14B8A6', '#64748B',
];
export const FILES_CONFIG = {
    maxFileSize: 50 * 1024 * 1024, // 50MB
    maxStoragePerUser: 500 * 1024 * 1024, // 500MB
    allowedMimeTypes: [
        'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml', 'image/bmp',
        'application/pdf',
        'text/plain', 'text/csv', 'text/html', 'text/css', 'text/javascript', 'text/markdown',
        'application/json', 'application/xml',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'application/zip', 'application/x-rar-compressed', 'application/gzip',
        'audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/webm',
        'video/mp4', 'video/webm', 'video/ogg',
    ],
    thumbnailSize: { width: 200, height: 200 },
    previewableTypes: [
        'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml',
        'application/pdf',
        'text/plain', 'text/csv', 'text/html', 'text/css', 'text/javascript', 'text/markdown',
        'application/json',
        'audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/webm',
        'video/mp4', 'video/webm', 'video/ogg',
    ],
};
export const AI_CHAT_ATTACHMENT_CONFIG = {
    maxFileSize: 10 * 1024 * 1024, // 10MB — MiniMax image understanding limit
    maxAttachments: 5,
    allowedMimeTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
    maxImageDimension: 2048, // px — resize threshold
};
export const ATTACHMENT_CONFIG = {
    maxFileSize: 10 * 1024 * 1024, // 10MB
    allowedMimeTypes: [
        'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml',
        'application/pdf',
        'text/plain', 'text/csv',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/zip',
        'application/json',
    ],
    thumbnailSize: { width: 200, height: 200 },
};
//# sourceMappingURL=index.js.map